import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Cpu, Brain, BarChart3, Bot, Zap, CheckCircle } from "lucide-react";
import { usePageSeo, BASE_URL } from "@/hooks/useSeo";

export default function KiBeratungPage() {
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  usePageSeo({
    title: "KI-Beratung & Automatisierung | LAB336 – KI für Schweizer KMU",
    description: "KI-Integration und intelligente Automatisierung für Schweizer KMU. Machine Learning, Predictive Analytics und Chatbots – wir machen KI nutzbar für Ihr Unternehmen.",
    canonical: `${BASE_URL}/ki-beratung`,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          "name": "KI-Beratung & Automatisierung",
          "description": "Integration von künstlicher Intelligenz und intelligenter Automatisierung in Geschäftsprozesse",
          "provider": { "@type": "Organization", "name": "LAB336", "url": BASE_URL },
          "areaServed": { "@type": "Country", "name": "Schweiz" },
          "serviceType": "KI-Beratung"
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": BASE_URL },
            { "@type": "ListItem", "position": 2, "name": "Leistungen", "item": `${BASE_URL}/#services` },
            { "@type": "ListItem", "position": 3, "name": "KI & Automatisierung", "item": `${BASE_URL}/ki-beratung` }
          ]
        }
      ]
    }
  });

  const goToScheduler = () => {
    navigate("/");
    setTimeout(() => {
      const el = document.querySelector("#scheduler");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 400);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="service-page-ki-beratung">
      <header className="bg-lab-primary text-white py-4 px-6 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-playfair text-xl tracking-wide">LAB<span className="text-lab-accent">336</span></Link>
          <Link to="/" className="text-sm text-white/80 hover:text-white flex items-center gap-1 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Startseite
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-lab-bg-light py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="w-16 h-16 bg-white flex items-center justify-center mb-6">
              <Cpu className="w-8 h-8 text-lab-accent" strokeWidth={1.5} />
            </div>
            <span className="text-lab-accent text-sm font-semibold tracking-widest uppercase">Leistung</span>
            <h1 className="font-playfair text-3xl lg:text-5xl font-semibold text-lab-primary mt-4 max-w-3xl">
              KI-Lösungen für Schweizer Unternehmen
            </h1>
            <p className="text-lab-text-muted text-lg mt-6 max-w-2xl leading-relaxed">
              Wir automatisieren die Zeitfresser in Schweizer KMU — mit KI, die sich in Wochen statt Monaten auszahlt. Praxisnah, messbar und ohne Techno-Overhead.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Nutzenargumentation */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="font-playfair text-2xl lg:text-3xl font-semibold text-lab-primary mb-6">Warum KI jetzt den Unterschied macht</h2>
            <div className="text-lab-text-muted leading-relaxed space-y-4">
              <p>
                Künstliche Intelligenz ist längst kein Thema mehr nur für Grosskonzerne. Auch Schweizer KMU können heute mit überschaubarem Aufwand enorme Effizienzgewinne erzielen — wenn sie wissen, wo der Hebel liegt. Das Problem: Viele Unternehmen wissen nicht, welche ihrer täglichen Abläufe durch KI automatisiert werden könnten, oder sie scheuen die vermeintliche Komplexität.
              </p>
              <p>
                Genau hier setzen wir an. Wir analysieren Ihre konkreten Geschäftsprozesse und identifizieren die Stellen, an denen KI den grössten Mehrwert bringt — sei es in der Kundenkommunikation, in der Datenauswertung oder bei repetitiven Verwaltungsaufgaben. Dabei setzen wir nicht auf theoretische Konzepte, sondern auf bewährte Technologien, die sich schnell und sicher in Ihre bestehende Infrastruktur integrieren lassen.
              </p>
              <p>
                Der entscheidende Vorteil: Sie müssen kein KI-Experte sein. Wir übersetzen komplexe Technologie in verständliche Lösungen, die Ihr Team sofort nutzen kann. Von der ersten Bestandsaufnahme bis zum produktiven Einsatz vergehen in der Regel nur wenige Wochen — nicht Monate. Und weil wir die Schweizer KMU-Landschaft kennen, wissen wir genau, welche Lösungen in welcher Unternehmensgrösse funktionieren.
              </p>
              <p>
                Das Ergebnis: Weniger Routinearbeit, schnellere Entscheidungen und ein Team, das sich endlich auf die wirklich wichtigen Aufgaben konzentrieren kann.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="bg-lab-bg-light py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="font-playfair text-2xl lg:text-3xl font-semibold text-lab-primary mb-4">KI im KMU-Alltag — konkrete Beispiele</h2>
            <p className="text-lab-text-muted mb-10">So setzen Schweizer Unternehmen KI bereits erfolgreich ein:</p>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  title: "Treuhandbüro mit 12 Mitarbeitenden",
                  problem: "Mitarbeitende verbrachten 40% ihrer Zeit mit dem Sortieren und Zuweisen eingehender Dokumente.",
                  solution: "KI-gestützte Dokumentenklassifikation und automatische Weiterleitung.",
                  result: "20 Stunden pro Woche eingespart, Bearbeitungszeit halbiert."
                },
                {
                  title: "Online-Händler mit 30 Mitarbeitenden",
                  problem: "Kundenanfragen per E-Mail wurden manuell beantwortet — mit Wartezeiten von 24-48 Stunden.",
                  solution: "KI-Chatbot für Standardanfragen, automatisches Ticket-Routing für komplexe Fälle.",
                  result: "80% der Anfragen sofort beantwortet, Kundenzufriedenheit +35%."
                },
                {
                  title: "Produktionsfirma mit 50 Mitarbeitenden",
                  problem: "Nachfrageprognosen basierten auf Bauchgefühl — mit regelmässigen Über- und Unterproduktionen.",
                  solution: "Predictive Analytics auf Basis historischer Verkaufsdaten und saisonaler Muster.",
                  result: "Prognosegenauigkeit von 60% auf 92% gesteigert, Lagerhaltungskosten -25%."
                },
              ].map((item) => (
                <div key={item.title} className="bg-white border border-lab-border p-6">
                  <h3 className="font-playfair text-lg font-semibold text-lab-primary mb-3">{item.title}</h3>
                  <div className="space-y-2 text-sm">
                    <p className="text-lab-text-muted"><span className="font-semibold text-lab-primary">Problem:</span> {item.problem}</p>
                    <p className="text-lab-text-muted"><span className="font-semibold text-lab-primary">Lösung:</span> {item.solution}</p>
                    <p className="text-lab-accent font-medium"><CheckCircle className="w-4 h-4 inline mr-1" />{item.result}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Leistungen */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-playfair text-2xl lg:text-3xl font-semibold text-lab-primary mb-8">Unsere KI-Leistungen im Detail</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { icon: Brain, title: "Machine Learning", text: "Massgeschneiderte ML-Modelle für Ihr Unternehmen: von Kundensegmentierung über Nachfrageprognosen bis zur Qualitätskontrolle. Wir nutzen Ihre vorhandenen Daten und machen sie zu einem echten Wettbewerbsvorteil." },
              { icon: BarChart3, title: "Predictive Analytics", text: "Datenbasierte Vorhersagen für bessere Entscheidungen: Umsatzprognosen, Kundenabwanderung erkennen, Lagerbestände optimieren. Sie handeln proaktiv statt reaktiv — und sparen dabei bares Geld." },
              { icon: Zap, title: "Prozessautomatisierung mit KI", text: "Intelligente Automatisierung für repetitive Aufgaben: Rechnungsverarbeitung, Dateneingabe, Reportings und E-Mail-Triage. Ihre Mitarbeitenden werden entlastet und können sich auf Wertschöpfung konzentrieren." },
              { icon: Bot, title: "Chatbots & KI-Assistenten", text: "Intelligente Chatbots für Kundenservice, interne FAQs oder Vertriebsunterstützung — trainiert auf Ihre Daten und Prozesse. Rund um die Uhr verfügbar, ohne zusätzliches Personal." },
            ].map((item) => (
              <motion.div key={item.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
                className="bg-lab-bg-light p-8 border border-lab-border">
                <item.icon className="w-6 h-6 text-lab-accent mb-4" strokeWidth={1.5} />
                <h3 className="font-playfair text-xl font-semibold text-lab-primary mb-3">{item.title}</h3>
                <p className="text-lab-text-muted leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vorgehen */}
      <section className="bg-lab-bg-dark py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-playfair text-2xl lg:text-3xl font-semibold text-white mb-12">Von der Idee zum produktiven KI-Einsatz</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: "01", title: "Discovery", text: "Use Cases identifizieren, Datenqualität prüfen und Machbarkeit realistisch bewerten — gemeinsam mit Ihrem Team." },
              { step: "02", title: "Proof of Concept", text: "Schneller Prototyp mit Ihren echten Daten. Sie sehen in 2-4 Wochen, was KI für Sie leisten kann." },
              { step: "03", title: "Entwicklung", text: "Produktionsreife Lösung entwickeln, in Ihre bestehenden Systeme integrieren und gründlich testen." },
              { step: "04", title: "Betrieb & Optimierung", text: "Monitoring, Retraining und kontinuierliche Verbesserung — damit Ihre KI-Lösung immer besser wird." },
            ].map((item, i) => (
              <motion.div key={item.step} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.15 }}
                className="text-white">
                <span className="text-lab-accent text-4xl font-playfair font-semibold">{item.step}</span>
                <h3 className="font-playfair text-xl font-semibold mt-4 mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-lab-primary py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-playfair text-2xl lg:text-3xl font-semibold text-white mb-4">
            Entdecken Sie, was KI in Ihrem Unternehmen bewirken kann
          </h2>
          <p className="text-gray-300 mb-4 leading-relaxed">
            In einem kostenlosen 30-Minuten-Gespräch zeigen wir Ihnen, welche Ihrer Prozesse sich mit KI automatisieren lassen — und welchen konkreten ROI Sie erwarten können.
          </p>
          <p className="text-lab-accent font-medium mb-8">
            Kein Fachchinesisch. Keine Verpflichtung. Nur eine ehrliche Einschätzung für Ihr Unternehmen.
          </p>
          <button onClick={goToScheduler} className="bg-lab-accent text-lab-primary font-semibold px-10 py-4 text-base inline-flex items-center gap-2 hover:bg-white transition-colors" data-testid="cta-scheduler">
            Kostenloses KI-Potenzial-Gespräch buchen <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>
    </div>
  );
}
